import { motion, useReducedMotion } from "framer-motion";
import { Wrench, BatteryCharging, PlugZap } from "lucide-react";
import { SITE } from "../data/site";
import { PhoneFloat } from "./PhoneFloat";
import { useTilt } from "../utils/tilt";

const float = (dur, delay = 0) => ({
  animate: { y: [0, -18, 0], rotate: [-3, 3, -3] },
  transition: { duration: dur, delay, repeat: Infinity, ease: "easeInOut" },
});

// Homepage hero figure: the 3 floating phones (iPhone, Samsung, Pixel) plus the
// enlarged floating logo, all inside a gyroscope / cursor 3D tilt stage.
export const HeroFigure = () => {
  const reduce = useReducedMotion();
  const f = (d, dl) => (reduce ? {} : float(d, dl));
  const { rotateX, rotateY } = useTilt(9);

  return (
    <div
      data-testid="hero-figure"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 flex items-center justify-center"
    >
      <motion.div
        className="absolute inset-0"
        style={reduce ? undefined : { rotateX, rotateY, transformPerspective: 1100, transformStyle: "preserve-3d" }}
      >
        {/* planet earth drifting slowly */}
        <motion.img
          src="/planets/earth.png"
          alt=""
          className="absolute right-[8%] top-[9%] block h-20 w-20 opacity-70 lg:right-[10%] lg:top-[6%] lg:h-40 lg:w-40 lg:opacity-85"
          style={{ filter: "drop-shadow(0 0 52px rgba(77,164,255,0.5))", z: -120 }}
          animate={reduce ? {} : { y: [0, -22, 0], rotate: [0, 10, 0], scale: [1, 1.05, 1] }}
          transition={{ duration: 30, repeat: Infinity, ease: "easeInOut" }}
        />

        <motion.div
          {...f(9, 0.6)}
          className="absolute left-[2%] top-[13%] block opacity-60 sm:top-[16%] sm:opacity-100 md:left-[3%]"
          style={{ z: 60 }}
          data-testid="phone-floating-samsung"
        >
          <PhoneFloat variant="samsung" imgClassName="h-[104px] max-w-[42vw] w-auto object-contain sm:h-[190px] sm:max-w-none lg:h-[230px]" caption={false} />
        </motion.div>

        {/* pixel rotates slowly while floating */}
        <motion.div
          {...f(7.5, 1.2)}
          className="absolute right-[4%] top-[18%] block opacity-60 sm:top-[24%] sm:opacity-100 md:right-[5%]"
          style={{ z: 90 }}
          data-testid="phone-floating-pixel"
        >
          <motion.div
            animate={reduce ? {} : { rotate: [0, 360] }}
            transition={{ duration: 55, repeat: Infinity, ease: "linear" }}
          >
            <PhoneFloat variant="pixel" imgClassName="h-[130px] sm:h-[220px] lg:h-[270px]" caption={false} />
          </motion.div>
        </motion.div>

        <motion.div
          {...f(8.5, 0.3)}
          className="absolute bottom-[4%] left-1/2 block -translate-x-1/2 opacity-70 sm:bottom-[5%] sm:opacity-100"
          style={{ z: 120 }}
          data-testid="phone-floating-iphone"
        >
          <PhoneFloat variant="iphone" imgClassName="h-[150px] sm:h-[210px] lg:h-[260px]" caption={false} />
        </motion.div>

        {/* enlarged floating brand mark with breathing aura — dead center */}
        <div className="absolute inset-0 flex items-center justify-center" style={{ transform: "translateZ(150px)" }}>
          <motion.div {...f(8)} className="aura-logo -mt-[10%]">
            <img
              src={SITE.logo}
              alt=""
              className="h-28 w-28 rounded-full object-cover ring-2 ring-gold/60 sm:h-36 sm:w-36"
              style={{ filter: "drop-shadow(0 0 48px rgba(201,155,63,0.7))" }}
            />
          </motion.div>
        </div>

        <motion.div {...f(10, 0.8)} className="absolute right-[20%] top-[12%] text-volt/60" style={{ z: 40 }}>
          <Wrench className="h-7 w-7" />
        </motion.div>
        <motion.div {...f(11, 1.5)} className="absolute bottom-[24%] right-[16%] text-magenta/60" style={{ z: 40 }}>
          <BatteryCharging className="h-6 w-6" />
        </motion.div>
        <motion.div {...f(9.5, 0.2)} className="absolute left-[22%] top-[8%] block text-cyan/60" style={{ z: 40 }}>
          <PlugZap className="h-6 w-6" />
        </motion.div>
      </motion.div>
    </div>
  );
};
